import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { Copy, Phone } from 'lucide-react';
import { ALFRED_MANAGE_PROMPT } from '@wix/ai-scheduling-assistant-constants';
import { Button, Card, Toast, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { queryKeys } from '../../../lib/query-client';
import { fetchAlfredSetup, fetchConnectors } from '../../../lib/connectors';
import { useAlfredConnect } from '../../../lib/use-alfred-connect';
import { useBasePath } from '../../../lib/use-base-path';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useSideChatFollow } from '../../../lib/use-side-chat-follow';
import { alfredSettingsEnabled } from '../../../lib/alfred-settings-flag';
import { ALFRED_SETTINGS_PATH } from '../../../lib/alfred-settings';
import { ALFRED_OUTREACH_ROW } from '../../../lib/outreach-alfred';
import { callsState, type CallsNumberLoad } from '../../../lib/outreach-calls';
import { AlfredSetupModal } from '../connectors/AlfredSetupModal';
import styles from './CallsBody.module.css';
import inbox from './contacts.module.css';

const PAGE = { page_name: 'contacts_page' } as const;
const SETUP = 'connectors.channelsOut.alfred.setup.';

/**
 * The Outreach screen's **Calls** tab (behind `symphonyOutreachCallTab`): Alfred, the AI phone receptionist,
 * while it is not set up, and the number it answers on once it is. Shared 1:1 with iOS/Android
 * `OutreachCallsTab`; what it draws is `callsState` (`lib/outreach-calls.ts`).
 *
 *  - **Not connected** → the pitch and **Get a Phone Number**, which is the Outreach Alfred row's own Connect
 *    (`useAlfredConnect`): the onboarding modal or the side-chat handoff.
 *  - **Connected** → "Alfred's phone number", the number, **Copy Number** (the E.164 number) and **Manage**, which
 *    is the row's Manage: Alfred's settings screen (`/alfred`) behind `alfredSettingsInSymphony`, else the chat.
 *
 * Layout: the Inbox tab's empty-state card, literally — it renders on the same `contacts.module.css` classes
 * (`emptyCard` / `emptyText`), so the two tabs cannot drift apart: a phone mark where the Inbox has its
 * illustration, the title, the body under it, then the action.
 *
 * Connected is the connector row's `connected`. The number is the setup read, asked only once connected; it is
 * the same cache entry the modal writes, so finishing the onboarding paints the number without another read.
 */
export function CallsBody() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const router = useRouter();
  const basePath = useBasePath();
  const connectors = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000 });
  const alfred = connectors.data?.connectors.find((c) => c.toolkit === ALFRED_OUTREACH_ROW.slug);
  const connected = alfred?.connected === true;
  const setup = useQuery({ queryKey: queryKeys.alfredSetup, queryFn: fetchAlfredSetup, enabled: connected, retry: false });
  useLoadBi('alfred-calls-number', { isLoading: setup.isFetching, isError: setup.isError }, { enabled: connected });

  const refreshConnectors = () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors });
  const alfredConnect = useAlfredConnect();
  // Manage's chat arm and the chat arm of Connect finish in the side chat over this screen, so the tab
  // re-reads while it is open, until the connected state flips.
  const followSideChat = useSideChatFollow(refreshConnectors, connected);
  const [toast, setToast] = useState<string | null>(null);

  // Warmed here so the Manage click finds the gate already answered (`useAlfredConnect` warms Connect's).
  useEffect(() => {
    void alfredSettingsEnabled();
  }, []);

  // A failed refetch keeps the last answer in `data`; nothing may show it while the error is up.
  const number: CallsNumberLoad = setup.isError
    ? { status: 'failed' }
    : setup.data
      ? { status: 'loaded', phoneNumber: setup.data.phoneNumber, displayPhoneNumber: setup.data.displayPhoneNumber }
      : { status: 'loading' };
  const state = callsState({ connectorsLoaded: connectors.data !== undefined || connectors.isError, alfred, number });

  const connectAlfred = () => {
    if (!alfred) return;
    logPageClickBi({ ...PAGE, button_name: 'calls_alfred_connect' });
    alfredConnect.start(alfred, followSideChat);
  };
  // The Outreach row's Manage: Alfred's settings screen while `alfredSettingsInSymphony` is on, else the chat.
  const manage = () => {
    logPageClickBi({ ...PAGE, button_name: 'calls_alfred_manage' });
    void alfredSettingsEnabled().then((settings) => {
      if (settings) {
        void router.push(`${basePath}${ALFRED_SETTINGS_PATH}`);
      } else {
        followSideChat();
        openSideChat({ kind: 'new', message: ALFRED_MANAGE_PROMPT });
      }
    });
  };
  const copy = async (e164: string) => {
    logPageClickBi({ ...PAGE, button_name: 'calls_copy_number' });
    try {
      await navigator.clipboard.writeText(e164);
      setToast(t(`${SETUP}number.copied`));
    } catch {
      // A denied clipboard leaves the number on screen to copy by hand.
    }
  };
  const retry = () => {
    logPageClickBi({ ...PAGE, button_name: 'calls_retry' });
    void setup.refetch();
  };

  const onNumber = state.kind === 'number' || state.kind === 'numberLoading' || state.kind === 'numberFailed';

  return (
    <div className={styles.root} data-testid="outreach-calls">
      {state.kind === 'loading' ? (
        <Card className={inbox.emptyCard} aria-busy>
          <span className={`orion-shimmer ${styles.skeletonMark}`} aria-hidden />
          <span className={`orion-shimmer ${styles.skeletonTitle}`} aria-hidden />
          <span className={`orion-shimmer ${styles.skeletonLine}`} aria-hidden />
        </Card>
      ) : (
        <Card className={inbox.emptyCard} aria-busy={state.kind === 'numberLoading'}>
          <span className={styles.phoneMark} aria-hidden>
            <Phone size={24} />
          </span>
          <div className={inbox.emptyText}>
            <Typography variant="h4" component="h2" color="strong">
              {onNumber ? t('outreach.calls.number.title') : t('outreach.calls.alfred.title')}
            </Typography>
            {onNumber ? (
              <>
                {state.kind === 'number' ? (
                  <Typography variant="h2" component="p" className={styles.number} dir="ltr">
                    {state.display}
                  </Typography>
                ) : state.kind === 'numberLoading' ? (
                  <span className={`orion-shimmer ${styles.numberSkeleton}`} aria-hidden />
                ) : null}
                <Typography variant="p3" color="secondary" className={styles.body}>
                  {state.kind === 'numberFailed' ? t(`${SETUP}error.load`) : t('outreach.calls.number.subtitle')}
                </Typography>
              </>
            ) : (
              <Typography variant="p3" color="secondary" className={styles.body}>
                {t('outreach.calls.alfred.pitch')}
              </Typography>
            )}
          </div>
          {state.kind === 'connect' && (
            <Button disabled={alfredConnect.busy} onClick={connectAlfred}>
              {t('outreach.calls.alfred.connect')}
            </Button>
          )}
          {onNumber && (
            <div className={styles.actions}>
              {state.kind === 'number' && (
                <Button startIcon={<Copy size={16} aria-hidden />} onClick={() => void copy(state.e164)}>
                  {t('outreach.calls.number.copy')}
                </Button>
              )}
              {state.kind === 'numberFailed' && <Button onClick={retry}>{t(`${SETUP}retry`)}</Button>}
              <Button variant="outlined" onClick={manage}>
                {t('common.manage')}
              </Button>
            </div>
          )}
        </Card>
      )}
      <AlfredSetupModal open={alfredConnect.setupOpen} onClose={alfredConnect.closeSetup} />
      <Toast open={toast !== null} message={toast ?? ''} severity="success" onClose={() => setToast(null)} />
    </div>
  );
}
